// @ts-nocheck
import { useMutation } from "@tanstack/react-query";
import type {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import type { ContactMessage } from "../../models";
import { customInstance } from "../../custom-instance";

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
  T
>() => T extends Y ? 1 : 2
  ? A
  : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Submit a contact form message
 */
export const contactCreate = (
  contactMessage: NonReadonly<ContactMessage>,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ContactMessage>(
    {
      url: `/api/contact/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: contactMessage,
      signal,
    },
    options
  );
};

export const getContactCreateMutationOptions = <
  TError = void,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof contactCreate>>,
    TError,
    { data: NonReadonly<ContactMessage> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof contactCreate>>,
  TError,
  { data: NonReadonly<ContactMessage> },
  TContext
> => {
  const mutationKey = ["contactCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof contactCreate>>,
    { data: NonReadonly<ContactMessage> }
  > = (props) => {
    const { data } = props ?? {};

    return contactCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ContactCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof contactCreate>>
>;
export type ContactCreateMutationBody = NonReadonly<ContactMessage>;
export type ContactCreateMutationError = void;

export const useContactCreate = <TError = void, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof contactCreate>>,
    TError,
    { data: NonReadonly<ContactMessage> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof contactCreate>>,
  TError,
  { data: NonReadonly<ContactMessage> },
  TContext
> => {
  const mutationOptions = getContactCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
